import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Faq from '../../components/Faq/Faq';
import Footer from '../../components/Footer/Footer';
import Menu from '../../components/Menu/Menu';
import Pagination from '../../components/Pagination/Pagination';
import Toast from '../../components/Toast/Toast';
import {
    deleteUser,
    getUsers,
    startUser,
    stopUser,
} from '../../services/UsersService';
import { confirmDelete } from '../../utils/confirmDialog';
import NewUserButton from './NewUserButton';
import SearchUser from './SearchUser';
import UserInfoModal from './UserInfoModal/UserInfoModal';
import UserModal from './UserModal/UserModal';
import UserRow from './UserRow';

function Users() {
    const defaultLocation = useLocation();

    const [users, setUsers] = useState([]);

    const [editUser, setEditUser] = useState({});

    const [viewUser, setViewUser] = useState({});

    const [count, setCount] = useState(0);

    const [search, setSearch] = useState('');

    const [page, setPage] = useState(getPage());

    const [notification, setNotification] = useState({ type: '', text: '' });

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        setPage(getPage(defaultLocation));
    }, [defaultLocation]);

    useEffect(() => {
        getUsers(search, page || 1)
            .then(result => {
                setUsers(result.rows);
                setCount(result.count);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }, [page, search]);

    function onViewClick(event) {
        const id = event.currentTarget.id.replace('view', '');
        setViewUser(users.find(u => String(u.id) === id) || {});
    }

    function onEditClick(event) {
        const id = event.target.id.replace('edit', '');
        setEditUser(users.find(u => u.id == id));
    }

    function onStopClick(event) {
        const id = event.target.id.replace('stop', '');
        stopUser(id)
            .then(result => {
                window.location.reload();
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onStartClick(event) {
        const id = event.target.id.replace('start', '');
        startUser(id)
            .then(result => {
                window.location.reload();
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        confirmDelete('This user will be permanently deleted!').then(
            confirmed => {
                if (!confirmed) return;
                deleteUser(id)
                    .then(result => {
                        window.location.reload();
                    })
                    .catch(err => {
                        console.error(
                            err.response ? err.response.data : err.message,
                        );
                        setNotification({
                            type: 'error',
                            text: err.response
                                ? err.response.data
                                : err.message,
                        });
                    });
            },
        );
    }

    function onSearchChange(event) {
        setSearch(event.target.value);
    }

    function onModalSubmit(event) {
        window.location.reload();
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h2 className="h4">Users</h2>
                    </div>
                    <div className="btn-toolbar mb-2 mb-md-0">
                        <div className="d-inline-flex align-items-center">
                            <NewUserButton />
                            <SearchUser
                                placeholder={search}
                                onChange={onSearchChange}
                            />
                        </div>
                    </div>
                </div>
                <div className="card card-body border-0 shadow table-wrapper table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th className="border-gray-200">Name</th>
                                <th className="border-gray-200">Email</th>
                                <th className="border-gray-200">Plan</th>
                                <th className="border-gray-200">Expires</th>
                                <th className="border-gray-200">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users && users.length ? (
                                users.map(user => (
                                    <UserRow
                                        key={user.id}
                                        data={user}
                                        onViewClick={onViewClick}
                                        onEditClick={onEditClick}
                                        onStartClick={onStartClick}
                                        onStopClick={onStopClick}
                                        onDeleteClick={onDeleteClick}
                                    />
                                ))
                            ) : (
                                <></>
                            )}
                        </tbody>
                    </table>
                    <Pagination count={count} />
                </div>
                <Faq data={FAQ_USERS} />
                <Footer />
            </main>
            <UserModal data={editUser} onSubmit={onModalSubmit} />
            <UserInfoModal data={viewUser} />
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

const FAQ_USERS = [
    {
        id: 1,
        title: 'New User - Creates a new user and sends welcome notifications if the account is active.',
        description: `This function handles the insertion of a new user in the system. It:<br>
        - Generates a random secure password<br>
        - Inserts the user into the database<br>
        - Sends welcome emails, SMS, or Telegram messages (if active and settings are available)<br>
        - Returns the created user (with sensitive fields like password removed)`,
    },
    {
        id: 2,
        title: 'Start User - Activates a user account and starts their data monitors.',
        description: `Starts a previously inactive user by:<br>
        - Setting <code>isActive</code> to true<br>
        - Starting related data and futures monitors<br>
        - Saving the updated user state<br>
        - Sending start notifications (email, SMS, push, Telegram)<br>
        - Returns the updated user object with sensitive fields cleared`,
    },
    {
        id: 3,
        title: 'Stop User - Deactivates a user account and stops all running processes.',
        description: `Stops a currently active user by:<br>
        - Setting <code>isActive</code> to false<br>
        - Stopping all user-related monitors and automations<br>
        - Saving the updated user state<br>
        - Sending stop notifications (email, SMS, push, Telegram)<br>
        - Returns the updated user object with sensitive fields cleared`,
    },
    {
        id: 4,
        title: 'Reset Password - Resets the user password and sends a notification.',
        description: `Resets a user's password by:<br>
        - Generating a new random password<br>
        - Updating the password in the database<br>
        - Sending reset alerts via email, SMS, or Telegram (if settings are available)<br>
        - Returns the updated user object with the password field cleared`,
    },
    {
        id: 5,
        title: 'Edit User - Updates a user and manages activation state.',
        description: `Updates user information with access control:<br>
        - Validates if the request is by an admin or the owner<br>
        - Applies updates to the user record<br>
        - If user activation state changes:<br>
        &nbsp;&nbsp;&nbsp;&nbsp;- Starts or stops monitors accordingly<br>
        &nbsp;&nbsp;&nbsp;&nbsp;- Sends activation/deactivation alerts<br>
        - Returns the updated user object`,
    },
    {
        id: 6,
        title: 'Delete User - Fully deletes a user and all related data from the system.',
        description: `This function performs a full user deletion:<br>
        - Stops monitors and sends alerts if user is active<br>
        - Begins a database transaction<br>
        - Deletes all user-related strategies, orders, monitors, automations, and templates<br>
        - Commits the transaction and removes the user record<br>
        - If an error occurs, the transaction is rolled back`,
    },
];

export default Users;
